import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, MapPin } from "lucide-react";

import properties from "../data/properties";
import PropertyCard from "../components/PropertyCard";

const Properties = () => {
  const sortedProperties = useMemo(() => {
    return [...properties].sort(
      (a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)
    );
  }, []);

  const featuredProperty =
    sortedProperties.find((property) => property.featured) ||
    sortedProperties[0];

  const otherProperties = sortedProperties.filter(
    (property) => property.id !== featuredProperty?.id
  );

  return (
    <>
      <Helmet>
        <title>
          Latest Properties | Solonet Properties Ghana
        </title>

        <meta
          name="description"
          content="Discover the latest homes, apartments and real estate opportunities available from Solonet Properties in Ghana."
        />

        <meta
          name="keywords"
          content="Solonet Properties, properties Ghana, houses for sale Accra, apartments Accra, property listings Ghana, luxury homes Ghana"
        />

        <meta
          property="og:title"
          content="Latest Properties | Solonet Properties"
        />

        <meta
          property="og:description"
          content="Explore the latest properties, homes and investment opportunities available from Solonet Properties."
        />

        <meta
          property="og:image"
          content={featuredProperty?.coverImage || "/images/header.jpg"}
        />

        <meta
          property="og:url"
          content="https://solonetproperties.com/properties"
        />

        <meta
          property="og:type"
          content="website"
        />
      </Helmet>

      <main className="bg-white min-h-screen">
        {/* HERO */}
        <section className="relative bg-[#0d1117] text-white pt-36 pb-24 md:pt-44 md:pb-32 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -right-40 -top-32 w-[500px] h-[500px] rounded-full border border-white/5" />
            <div className="absolute -right-20 -top-12 w-[320px] h-[320px] rounded-full border border-white/5" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <p className="text-xs md:text-sm uppercase tracking-[0.32em] text-blue-400 font-semibold">
                Solonet Property Collection
              </p>

              <h1 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.05] tracking-tight">
                Find a place worth
                <span className="block text-gray-400">
                  calling home.
                </span>
              </h1>

              <p className="mt-7 max-w-2xl text-base md:text-lg leading-8 text-gray-400">
                Explore our latest residential properties and
                real estate opportunities across Accra and
                beyond, carefully selected by Solonet
                Properties.
              </p>
            </motion.div>
          </div>
        </section>

        {/* FEATURED PROPERTY */}
        {featuredProperty && (
          <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-blue-600 font-semibold">
                  Latest Property
                </p>

                <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-gray-900">
                  Featured by Solonet
                </h2>
              </div>

              <p className="hidden md:block text-sm text-gray-400 max-w-sm text-right leading-6">
                Discover one of our latest opportunities
                currently available.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8 }}
              className="grid lg:grid-cols-[1.35fr_0.65fr] bg-gray-50"
            >
              {/* IMAGE */}
              <Link
                to={`/properties/${featuredProperty.slug}`}
                className="group relative min-h-[420px] md:min-h-[600px] overflow-hidden"
              >
                <img
                  src={featuredProperty.coverImage}
                  alt={featuredProperty.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />

                <div className="absolute left-6 top-6 md:left-8 md:top-8">
                  <span className="bg-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-gray-900">
                    {featuredProperty.status}
                  </span>
                </div>

                <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                    <ArrowUpRight
                      size={21}
                      className="text-gray-900"
                    />
                  </div>
                </div>
              </Link>

              {/* CONTENT */}
              <div className="flex flex-col justify-between px-7 py-10 md:px-12 md:py-14 lg:px-14">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">
                    {featuredProperty.category}
                  </p>

                  <h3 className="mt-5 text-3xl md:text-5xl font-semibold text-gray-900 leading-tight">
                    {featuredProperty.title}
                  </h3>

                  <div className="mt-5 flex items-center gap-2 text-gray-500">
                    <MapPin size={18} strokeWidth={1.7} />

                    <span className="text-sm md:text-base">
                      {featuredProperty.location}
                    </span>
                  </div>

                  <p className="mt-7 text-gray-600 leading-8">
                    {featuredProperty.excerpt}
                  </p>

                  {(featuredProperty.bedrooms ||
                    featuredProperty.bathrooms) && (
                    <div className="mt-8 flex gap-8 border-t border-gray-200 pt-7">
                      {featuredProperty.bedrooms && (
                        <div>
                          <p className="text-2xl font-semibold text-gray-900">
                            {featuredProperty.bedrooms}
                          </p>

                          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-gray-400">
                            Bedrooms
                          </p>
                        </div>
                      )}

                      {featuredProperty.bathrooms && (
                        <div>
                          <p className="text-2xl font-semibold text-gray-900">
                            {featuredProperty.bathrooms}
                          </p>

                          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-gray-400">
                            Bathrooms
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-12">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-semibold">
                    {featuredProperty.priceLabel}
                  </p>

                  <p className="mt-2 text-2xl md:text-3xl font-semibold text-gray-900">
                    {featuredProperty.price}
                  </p>

                  <Link
                    to={`/properties/${featuredProperty.slug}`}
                    className="mt-8 inline-flex items-center gap-3 text-sm font-semibold text-gray-900 hover:text-blue-600 transition"
                  >
                    Explore Property

                    <ArrowUpRight size={17} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </section>
        )}

        {/* PROPERTY COLLECTION */}
        <section className="bg-[#f7f7f5]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="max-w-3xl mb-14 md:mb-18">
              <p className="text-xs uppercase tracking-[0.28em] text-blue-600 font-semibold">
                Latest Properties
              </p>

              <h2 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-gray-900">
                More from Solonet Properties
              </h2>

              <p className="mt-5 text-gray-600 leading-7 max-w-2xl">
                Browse our current selection of residential
                properties, homes for sale and rental
                opportunities.
              </p>
            </div>

            {otherProperties.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 lg:gap-x-12 gap-y-16">
                {otherProperties.map((property, index) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center border border-gray-200 bg-white">
                <p className="text-gray-500">
                  More properties will be published soon.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* CONTACT CTA */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="border-t border-gray-200 pt-14 md:pt-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
              <div className="max-w-3xl">
                <p className="text-xs uppercase tracking-[0.28em] text-blue-600 font-semibold">
                  Property Enquiries
                </p>

                <h2 className="mt-4 text-3xl md:text-5xl leading-tight font-semibold text-gray-900">
                  Seen a property that
                  <span className="block text-gray-400">
                    interests you?
                  </span>
                </h2>

                <p className="mt-5 text-gray-600 leading-7 max-w-xl">
                  Speak with Solonet Properties for
                  availability, pricing, viewings and further
                  information.
                </p>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 bg-[#111827] text-white px-8 py-4 text-sm font-semibold hover:bg-blue-600 transition-colors duration-300"
              >
                Contact Solonet

                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Properties;